import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { FinancialInstitutionRequest } from '@app/core/models/financial-institution/financial-institution-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SharedModule } from '@app/shared/shared.module';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { MenuItem } from 'primeng/api';

@Component({
  templateUrl: './financial-institution-register.component.html',
  styleUrls: ['./financial-institution-register.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class FinancialInstitutionRegisterComponent extends VaultBaseComponent implements OnInit {
  defaultLanguage: string = 'pt-BR';
  financialId: string = '';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  selectedStatus: Option | null = {
    label: this.t(this.tk.User.Active),
    value: '1',
  };
  financialInstitutionPermissions = Permissions.FinancialInstitution;
  public financialInstitutionRegisterProps = nameof<FinancialInstitutionRequest>();

  financialInstitutionForm = this.formBuilder.group({
    [this.financialInstitutionRegisterProps.id]: [{ value: '', disabled: true }],
    [this.financialInstitutionRegisterProps.status]: [''],
    [this.financialInstitutionRegisterProps.institutionName]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.compensationCode]: [
      '',
      [Validators.required, Validators.minLength(3)],
    ],
    [this.financialInstitutionRegisterProps.ispb]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.shortName]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.displayName]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.network]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.url]: ['', [Validators.required]],
    [this.financialInstitutionRegisterProps.cnpj]: ['', [Validators.required]],
  });

  userStatusOptions: Option[] = [
    { label: this.t(this.tk.User.Active), value: '1' },
    { label: this.t(this.tk.User.Inactive), value: '0' },
  ];

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private financialInstitutionService: FinancialInstitutionService,
    private formErrorHandler: FormErrorHandlerService,
    private toast: ToastHelperService
  ) {
    super(translateService);
    this.defaultLanguage = translateService.currentLang;
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.General.Groups),
        routerLink: '/dashboard/financial-institution/list',
      },
      {
        label: this.t(this.tk.General.Register),
        routerLink: '/dashboard/financial-institution/register',
      },
    ];

    this.getVerifyFinancialInstitutionId();
  }

  getVerifyFinancialInstitutionId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.financialId = params['id'];
        this.utilsService.setIsLoading(true);
        this.financialInstitutionService.getPermissionGroupById(this.financialId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              const financialInstitution = response;

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.id
              ].setValue(financialInstitution.id || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.institutionName
              ].setValue(financialInstitution.institutionName || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.compensationCode
              ].setValue(financialInstitution.compensationCode || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.ispb
              ].setValue(financialInstitution.ispb || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.shortName
              ].setValue(financialInstitution.shortName || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.displayName
              ].setValue(financialInstitution.displayName || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.network
              ].setValue(financialInstitution.network || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.url
              ].setValue(financialInstitution.url || '');

              this.financialInstitutionForm.controls[
                this.financialInstitutionRegisterProps.cnpj
              ].setValue(financialInstitution.cnpj || '');
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error(error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingFinancialInstitution),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      }
    });
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    switch (key) {
      case 'selectedStatus':
        this.selectedStatus = selectedOptions;
        this.financialInstitutionForm.controls[
          this.financialInstitutionRegisterProps.status
        ].setValue(selectedOptions?.value || '');
        break;
    }
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/financial-institution/list']);
  }

  registerFinancialInstitution(): void {
    if (this.financialInstitutionForm.invalid) {
      this.financialInstitutionForm.markAllAsTouched();
      return;
    }

    const formValue =
      this.financialInstitutionForm.getRawValue() as unknown as FinancialInstitutionRequest;

    if (this.financialId) {
      this.editFinancialInstitution(formValue);
    } else {
      this.postFinancialInstitution(formValue);
    }
  }

  editFinancialInstitution(formValue: FinancialInstitutionRequest): void {
    this.utilsService.setIsLoading(true);
    this.financialInstitutionService
      .putFinancialInstitution(formValue, this.financialId)
      .subscribe({
        next: response => {
          this.utilsService.setIsLoading(false);
          this.toast.showToastSuccess(
            this.t(this.tk.Management.FinancialInstitutionUpdatedSuccessfully),
            response?.message || ''
          );
          if (response) {
            this.router.navigate(['dashboard/financial-institution/list']);
          }
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          if (error instanceof ErrorResponse) {
            this.formErrorHandler.applyFormErrors(this.financialInstitutionForm, error);
          } else {
            console.log('editPermissionGroup error: ', error);
          }
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorUpdatingFinancialInstitution),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  postFinancialInstitution(formValue: FinancialInstitutionRequest): void {
    this.utilsService.setIsLoading(true);
    this.financialInstitutionService.postFinancialInstitution(formValue).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(
          this.t(this.tk.Management.FinancialInstitutionCreatedSuccessfully),
          response?.message || ''
        );
        this.router.navigate(['dashboard/financial-institution/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.financialInstitutionForm, error);
        } else {
          console.log('postPermissionGroup error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingFinancialInstitution),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
}
